/**
 * Terms of Service — Static legal page.
 * Route: /#/legal/terms
 *
 * Plain English, professional tone. Reviewed periodically by Mi6 Group.
 */
import LegalLayout, { legalH2, legalP, legalUl } from "./LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="May 16, 2026">
      <p style={legalP}>
        These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of the
        Attilius platform, the Mi6 Group websites, and any related services
        (collectively, the &ldquo;Service&rdquo;). The Service is provided by Mi6 Group
        (&ldquo;Mi6&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;). By accessing or using the Service you agree to
        be bound by these Terms.
      </p>

      <h2 style={legalH2}>1. Eligibility and account access</h2>
      <p style={legalP}>
        The Service is intended for use by employees and contractors of organizations
        that hold a current subscription with Mi6. You must be at least 18 years of
        age and authorized by your employer to use the Service. You are responsible
        for keeping your credentials confidential and for all activity that occurs
        under your account. Notify support_team@mi6group.com promptly if you suspect
        unauthorized use.
      </p>

      <h2 style={legalH2}>2. Acceptable use</h2>
      <p style={legalP}>You agree not to:</p>
      <ul style={legalUl}>
        <li>Reverse engineer, copy, or attempt to extract the source code of the Service.</li>
        <li>Use the Service to store or transmit unlawful, infringing, or harmful content.</li>
        <li>Interfere with the security or integrity of the Service or other tenants.</li>
        <li>Use the Service to send unsolicited bulk communications.</li>
        <li>Resell or sublicense the Service without prior written consent from Mi6.</li>
      </ul>

      <h2 style={legalH2}>3. Customer data</h2>
      <p style={legalP}>
        You retain all rights to the data you and your colleagues submit to the
        Service (&ldquo;Customer Data&rdquo;). You grant Mi6 a limited license to host, process,
        and display Customer Data solely to operate the Service for your
        organization. Mi6 does not sell Customer Data and does not use Customer
        Data to train third-party AI models without explicit written permission.
      </p>

      <h2 style={legalH2}>4. Subscription, fees, and renewals</h2>
      <p style={legalP}>
        Subscription terms, pricing, billing cycle, and payment method are
        established in the order form or signed agreement between Mi6 and your
        organization. Subscriptions auto-renew at the end of each term unless
        either party gives written notice of non-renewal at least thirty (30) days
        before the renewal date.
      </p>

      <h2 style={legalH2}>5. Service availability</h2>
      <p style={legalP}>
        Mi6 will use commercially reasonable efforts to keep the Service available.
        We may schedule maintenance windows and will notify customers in advance
        whenever practical. The Service is offered &ldquo;as is&rdquo; and Mi6 does not
        guarantee uninterrupted or error-free operation.
      </p>

      <h2 style={legalH2}>6. Confidentiality</h2>
      <p style={legalP}>
        Each party agrees to protect the other&rsquo;s confidential information with the
        same degree of care it uses to protect its own confidential information,
        and in no event less than reasonable care. Customer Data is treated as
        confidential information of your organization.
      </p>

      <h2 style={legalH2}>7. Intellectual property</h2>
      <p style={legalP}>
        The Service, including all software, designs, methodology, training
        material, and documentation, is the exclusive property of Mi6 Group or its
        licensors and is protected by intellectual property laws. These Terms do
        not transfer any ownership rights to you.
      </p>

      <h2 style={legalH2}>8. Termination</h2>
      <p style={legalP}>
        Either party may terminate access for material breach if the breach is not
        cured within thirty (30) days of written notice. Upon termination your
        right to access the Service ends, and Mi6 will make Customer Data
        available for export for thirty (30) days, after which it may be deleted
        in accordance with the Privacy Policy.
      </p>

      <h2 style={legalH2}>9. Disclaimers</h2>
      <p style={legalP}>
        To the maximum extent permitted by law, Mi6 disclaims all warranties,
        express or implied, including merchantability, fitness for a particular
        purpose, and non-infringement. Recommendations generated by the Service
        (including AI-assisted suggestions) are aids to professional judgment, not
        substitutes for it.
      </p>

      <h2 style={legalH2}>10. Limitation of liability</h2>
      <p style={legalP}>
        To the maximum extent permitted by law, Mi6&rsquo;s aggregate liability for any
        claim arising out of or relating to the Service will not exceed the fees
        paid by your organization to Mi6 for the Service during the twelve (12)
        months preceding the event giving rise to the claim. Neither party will
        be liable for indirect, incidental, or consequential damages.
      </p>

      <h2 style={legalH2}>11. Changes to the Service or Terms</h2>
      <p style={legalP}>
        Mi6 may update the Service and these Terms from time to time. Material
        changes will be communicated by email to the customer&rsquo;s administrator at
        least thirty (30) days before they take effect. Continued use after the
        effective date constitutes acceptance.
      </p>

      <h2 style={legalH2}>12. Governing law</h2>
      <p style={legalP}>
        These Terms are governed by the laws of the State of Michigan, United
        States, without regard to its conflict of laws principles. The parties
        consent to the exclusive jurisdiction of the state and federal courts
        located in Oakland County, Michigan.
      </p>

      <h2 style={legalH2}>13. Contact</h2>
      <p style={legalP}>
        Questions about these Terms can be sent to{" "}
        <a href="mailto:support_team@mi6group.com">support_team@mi6group.com</a>.
      </p>
    </LegalLayout>
  );
}
